// src/features/text/components/TextToolWorkspace.tsx
import React from 'react';
import {
    Box,
    Grid,
    Paper,
    TextField,
    Typography,
    IconButton,
    Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import TextStats from './TextStats';
import { getTextStats } from '../utils/textUtils';

interface TextToolWorkspaceProps {
    inputText: string;
    outputText: string;
    onInputChange: (value: string) => void;
    inputLabelKey: string;
    outputLabelKey: string;
    children?: React.ReactNode; // For tool-specific options
}

const TextToolWorkspace: React.FC<TextToolWorkspaceProps> = ({
                                                                 inputText,
                                                                 outputText,
                                                                 onInputChange,
                                                                 inputLabelKey,
                                                                 outputLabelKey,
                                                                 children,
                                                             }) => {
    const { t } = useTranslation('tools');

    const handleCopyToClipboard = async (text: string) => {
        if (navigator.clipboard && text) {
            await navigator.clipboard.writeText(text);
            // Optionally, add a snackbar notification here for better UX
        }
    };

    const inputStats = React.useMemo(() => getTextStats(inputText), [inputText]);
    const outputStats = React.useMemo(() => getTextStats(outputText), [outputText]);

    return (
        <Paper
            elevation={0}
            variant="outlined"
            sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}
        >
            <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                minHeight: { xs: 'auto', md: '400px' }
            }}>
                {/* Input Area */}
                <Box sx={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: { xs: '300px', md: 'auto' }
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" component="h3" gutterBottom>
                            {t(inputLabelKey)}
                        </Typography>
                        <Tooltip title={t('text_common_clear_input')}>
                            <span>
                                <IconButton onClick={() => onInputChange('')} size="small" disabled={!inputText}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                    <TextField
                        multiline
                        rows={12}
                        fullWidth
                        variant="outlined"
                        value={inputText}
                        onChange={(e) => onInputChange(e.target.value)}
                        placeholder={t('text_common_input_placeholder')}
                        sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'action.hover',
                                height: '100%',
                            },
                            '& .MuiOutlinedInput-input': {
                                height: '100% !important',
                            }
                        }}
                    />
                    <TextStats stats={inputStats} />
                </Box>

                {/* Output Area */}
                <Box sx={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: { xs: '300px', md: 'auto' }
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" component="h3" gutterBottom>
                            {t(outputLabelKey)}
                        </Typography>
                        <Tooltip title={t('text_common_copy_output')}>
                             <span>
                                <IconButton onClick={() => handleCopyToClipboard(outputText)} size="small" disabled={!outputText}>
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                             </span>
                        </Tooltip>
                    </Box>
                    <TextField
                        multiline
                        rows={12}
                        fullWidth
                        variant="outlined"
                        value={outputText}
                        InputProps={{
                            readOnly: true,
                        }}
                        sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'action.hover',
                                height: '100%',
                            },
                            '& .MuiOutlinedInput-input': {
                                height: '100% !important',
                            },
                            '& .MuiInputBase-input.Mui-disabled': {
                                WebkitTextFillColor: 'text.primary', // Ensure text is readable
                                color: 'text.primary',
                            },
                        }}
                    />
                    <TextStats stats={outputStats} />
                </Box>
            </Box>

            {/* Tool-specific options area */}
            {children && (
                <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                    {children}
                </Box>
            )}
        </Paper>
    );
};

export default TextToolWorkspace;